import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Ruler, Edit2, Trash2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { instrumentService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CalibrationStatusBadge, { CAL_STATUSES, INSTRUMENT_TYPES } from '../components/CalibrationStatusBadge';

const emptyForm = {
  code: '',
  name: '',
  instrument_type: 'calibro',
  manufacturer: '',
  model: '',
  serial_number: '',
  measurement_range: '',
  resolution: '',
  accuracy: '',
  location: '',
  owner: '',
  calibration_interval_months: 12,
  notes: '',
};

export default function InstrumentsPage() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', instrument_type: '', calibration_status: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.keys(filters).forEach(k => { if (filters[k]) params[k] = filters[k]; });
      const r = await instrumentService.getAll(params);
      setItems(r.data || []);
    } catch {
      toast.error('Errore caricamento strumenti');
    } finally { setLoading(false); }
  };

  const counts = useMemo(() => ({
    total: items.length,
    valida: items.filter(i => i.calibration_status === 'valida').length,
    in_scadenza: items.filter(i => i.calibration_status === 'in_scadenza').length,
    scaduta: items.filter(i => i.calibration_status === 'scaduta').length,
    non_disponibile: items.filter(i => i.calibration_status === 'non_disponibile').length,
  }), [items]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (i) => { setEditing(i); setForm({ ...emptyForm, ...i }); setDialogOpen(true); };

  const handleSubmit = async () => {
    if (!form.code || !form.name) { toast.error('Codice e nome sono obbligatori'); return; }
    try {
      const payload = { ...form };
      if (editing) await instrumentService.update(editing.id, payload);
      else await instrumentService.create(payload);
      toast.success(editing ? 'Strumento aggiornato' : 'Strumento creato');
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async () => {
    try {
      await instrumentService.delete(deleteId);
      toast.success('Strumento eliminato');
      setDeleteId(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const typeLabel = (v) => INSTRUMENT_TYPES.find(t => t.value === v)?.label || v;

  return (
    <div className="space-y-6" data-testid="instruments-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Ruler className="w-6 h-6 text-slate-700" />
            Registro Strumenti di Misura
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestione tarature di calibri, micrometri, comparatori e altri strumenti</p>
        </div>
        {hasRole('admin', 'quality') && (
          <Button onClick={openCreate} className="h-10 px-4" data-testid="add-instrument-btn">
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Strumento
          </Button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs uppercase font-bold text-slate-500">Totale</p><p className="text-2xl font-bold mt-1">{counts.total}</p></CardContent></Card>
        <Card className="border-l-4 border-l-emerald-500"><CardContent className="p-4"><p className="text-xs uppercase font-bold text-emerald-700">Valide</p><p className="text-2xl font-bold text-emerald-600 mt-1">{counts.valida}</p></CardContent></Card>
        <Card className="border-l-4 border-l-amber-500"><CardContent className="p-4"><p className="text-xs uppercase font-bold text-amber-700">In Scadenza</p><p className="text-2xl font-bold text-amber-600 mt-1">{counts.in_scadenza}</p></CardContent></Card>
        <Card className="border-l-4 border-l-red-500"><CardContent className="p-4"><p className="text-xs uppercase font-bold text-red-700">Scadute</p><p className="text-2xl font-bold text-red-600 mt-1">{counts.scaduta}</p></CardContent></Card>
        <Card className="border-l-4 border-l-slate-400"><CardContent className="p-4"><p className="text-xs uppercase font-bold text-slate-600">Mai Tarato</p><p className="text-2xl font-bold text-slate-700 mt-1">{counts.non_disponibile}</p></CardContent></Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" />Filtri</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Ricerca (codice/nome/SN)</Label>
              <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="es. CAL-001" data-testid="filter-search" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tipo</Label>
              <Select value={filters.instrument_type || 'all'} onValueChange={v => setFilters(f => ({ ...f, instrument_type: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {INSTRUMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Stato Taratura</Label>
              <Select value={filters.calibration_status || 'all'} onValueChange={v => setFilters(f => ({ ...f, calibration_status: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {CAL_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={() => setFilters({ search: '', instrument_type: '', calibration_status: '' })}>Reset filtri</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"></div></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500"><Ruler className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p>Nessuno strumento</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Codice</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Nome</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Tipo</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">SN</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Ultima Taratura</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Prossima Taratura</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Stato</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(i => (
                    <tr key={i.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`instrument-row-${i.id}`}>
                      <td className="px-3 py-2 font-mono font-medium">
                        <Link to={`/instruments/${i.id}`} className="text-sky-600 hover:text-sky-800">{i.code}</Link>
                      </td>
                      <td className="px-3 py-2"><Link to={`/instruments/${i.id}`} className="text-slate-900 hover:text-sky-700 font-medium">{i.name}</Link></td>
                      <td className="px-3 py-2 text-slate-600">{typeLabel(i.instrument_type)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{i.serial_number || '-'}</td>
                      <td className="px-3 py-2">{i.last_calibration_date || '-'}</td>
                      <td className="px-3 py-2 font-medium">{i.next_calibration_date || '-'}</td>
                      <td className="px-3 py-2"><CalibrationStatusBadge status={i.calibration_status} days={i.days_to_expiry} /></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Link to={`/instruments/${i.id}`}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><ExternalLink className="w-4 h-4" /></Button>
                        </Link>
                        {hasRole('admin', 'quality') && (
                          <>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(i)} data-testid={`edit-${i.id}`}><Edit2 className="w-4 h-4" /></Button>
                            {hasRole('admin') && (
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => setDeleteId(i.id)} data-testid={`delete-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Modifica Strumento' : 'Nuovo Strumento di Misura'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-4">
            <div className="space-y-1"><Label>Codice *</Label><Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="es. CAL-001" data-testid="inst-code" /></div>
            <div className="space-y-1"><Label>Nome *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="es. Calibro digitale 150mm" data-testid="inst-name" /></div>
            <div className="space-y-1"><Label>Tipo</Label>
              <Select value={form.instrument_type} onValueChange={v => setForm(f => ({ ...f, instrument_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INSTRUMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Numero di Serie</Label><Input value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Costruttore</Label><Input value={form.manufacturer} onChange={e => setForm(f => ({ ...f, manufacturer: e.target.value }))} placeholder="es. Mitutoyo" /></div>
            <div className="space-y-1"><Label>Modello</Label><Input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Range Misura</Label><Input value={form.measurement_range} onChange={e => setForm(f => ({ ...f, measurement_range: e.target.value }))} placeholder="es. 0-150 mm" /></div>
            <div className="space-y-1"><Label>Risoluzione</Label><Input value={form.resolution} onChange={e => setForm(f => ({ ...f, resolution: e.target.value }))} placeholder="es. 0.01 mm" /></div>
            <div className="space-y-1"><Label>Accuratezza</Label><Input value={form.accuracy} onChange={e => setForm(f => ({ ...f, accuracy: e.target.value }))} placeholder="es. ±0.02 mm" /></div>
            <div className="space-y-1"><Label>Ubicazione</Label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="es. Sala metrologica" /></div>
            <div className="space-y-1"><Label>Responsabile</Label><Input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Intervallo Taratura (mesi)</Label><Input type="number" min="1" value={form.calibration_interval_months} onChange={e => setForm(f => ({ ...f, calibration_interval_months: parseInt(e.target.value) || 12 }))} /></div>
            <div className="space-y-1 md:col-span-2"><Label>Note</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSubmit} data-testid="inst-submit-btn">{editing ? 'Aggiorna' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Conferma eliminazione</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 py-2">Eliminare definitivamente lo strumento e tutte le sue tarature?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete}>Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
